
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Project {
  id: string;
  title: string;
  description: string;
  image_url: string | null;
  project_url: string | null;
  github_url: string | null;
  technologies: string[] | null;
  created_at: string;
  display_order: number | null;
}

const ProjectsPage = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { user, hasRole } = useAuth();

  const canCreateProject = user && (hasRole("admin") || hasRole("manager") || hasRole("super_admin"));

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .order('display_order', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false });
        
        if (error) {
          throw error;
        }
        
        console.log("Fetched projects:", data);
        const projectsWithDisplayOrder = data?.map(project => ({
          ...project,
          display_order: project.display_order || null
        })) || [];
        setProjects(projectsWithDisplayOrder);
      } catch (error: any) {
        console.error('Error fetching projects:', error.message);
        toast({
          title: "Failed to load projects",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, [toast]);

  return (
    <Layout>
      <div className="section-container">
        <h1 className="text-4xl font-bold mb-8">
          <span className="text-[#34BF49]">Projects</span>
        </h1>
        
        <p className="text-gray-600 mb-8">
          Explore a collection of my latest projects. These represent my work in web development, 
          showcasing a variety of technologies and solutions I've implemented for real-world challenges.
        </p>
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse">Loading projects...</div>
          </div>
        ) : projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="flex flex-col h-full">
                <div className="aspect-video bg-gray-100 overflow-hidden">
                  <img 
                    src={project.image_url || "/placeholder.svg"} 
                    alt={project.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardHeader className="pb-2">
                  <h3 className="text-xl font-bold">{project.title}</h3>
                </CardHeader>
                <CardContent className="pb-4 flex-grow">
                  <p className="text-gray-600 mb-4 line-clamp-3">{project.description}</p>
                  {project.technologies && project.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {project.technologies.map((tech, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-0 flex gap-2">
                  {project.project_url && (
                    <Button variant="outline" size="sm" asChild className="flex-1">
                      <a href={project.project_url} target="_blank" rel="noopener noreferrer">
                        Website
                      </a>
                    </Button>
                  )}
                  {project.github_url && (
                    <Button variant="outline" size="sm" asChild className="flex-1">
                      <a href={project.github_url} target="_blank" rel="noopener noreferrer">
                        GitHub
                      </a>
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-lg text-gray-600 mb-6">No projects found.</p>
            {canCreateProject && (
              <Link to="/projects/create">
                <Button className="flex items-center gap-2 mx-auto">
                  <PlusCircle size={18} />
                  Add Your First Project
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProjectsPage;
